import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kSettleOnce } from "../../../Admin/kReusableCoreTS/kSettleOnce";

// WaCrypt15 - opens a WhatsApp `.crypt15` database in the BROWSER, so the key never leaves it.
//
// ==================================================================================================
// !! WHY THIS EXISTS: IT IS WHAT MAKES A SERVER-SIDE IMPORT SAFE
// ==================================================================================================
//
// The import used to be impossible to host, and the reason was never the decryption - it was the
// FETCH. `wabdd` reads the backup out of Google by impersonating the WhatsApp Android app, and that
// needs a Google MASTER TOKEN: the Android device credential for the subscriber's whole account. No
// hosted service should ever hold one, and fifty in one container is a breach costing fifty entire
// Google accounts.
//
// ⭐ SO THE FETCH IS REMOVED RATHER THAN SECURED. The subscriber supplies the `.crypt15` file
// themselves, this decrypts it HERE, and the plain database goes up the Drive channel the service
// already reads. The heavy work - dossiers, transcripts, analysis - becomes server-side, which is
// the part that actually had to be. Nothing in this flow can ask Google for anything.
//
// ⛔ AND THE 64-DIGIT KEY NEVER CROSSES THE NETWORK. It could have: the server could decrypt if the
// browser posted both. That would work and it would be worse - the key opens every backup that
// account will ever make, WhatsApp cannot rotate it for them, and a key in a request body is a key
// in a log, a proxy buffer and a crash dump. Doing it here costs one file read and removes the
// entire question.
//
// ==================================================================================================
// !! THE FORMAT, READ OFF `wa_crypt_tools` 0.1.0 RATHER THAN OFF DOCUMENTATION
// ==================================================================================================
//
//   [0]                     protobuf length          (1 byte)
//   [1]                     0x01 feature flag        (present ONLY when it is 0x01)
//   [.. hdrEnd)             protobuf BackupPrefix
//   [hdrEnd .. len-16)      AES-256-GCM ciphertext followed by its 16-byte tag
//   [len-16 .. len)         MD5 of everything before it
//
// ⚠ NO PROTOBUF LIBRARY. Exactly one field is needed - the 16-byte IV at field 3, sub-field 1 - and
// a 20-line scan finds it. Verified against a real 26.7 MB `msgstore.db.crypt15`: header ends at
// 137, IV `cd2a537a...`.
//
// ⚠ THE MD5 TRAILER IS DELIBERATELY NOT CHECKED. It is not in WebCrypto, and it is redundant: GCM
// already authenticates every byte it covers, and a tag that verifies is a stronger statement than
// an MD5 that matches. Shipping an MD5 to re-check what AES-GCM just proved would be ceremony.
//
// ⚠ THE KEY DERIVATION IS PLAIN HKDF-SHA256, which is not obvious from the Python: `encryptionloop`
// hand-rolls HMAC extract-and-expand with a 32-zero-byte seed, and that IS HKDF with the RFC 5869
// default salt. `info` is the literal `backup encryption`, 17 bytes, no NUL.

/** The opened database, or the sentence that refuses it. Never both. */
export class Crypt15Result {
    public Db: Uint8Array | null = null;
    public Why: string = "";
}

export type Crypt15Done = (Result: Crypt15Result) => void;

export class WaCrypt15 {
    /** The HKDF context. ⛔ EXACTLY THESE BYTES - a trailing NUL or a capital derives a wrong key. */
    public static readonly Info: string = "backup encryption";

    /** What every SQLite file starts with. The proof that the right thing came out. */
    public static readonly Magic: string = "SQLite format 3";

    /**
     * The 32 raw bytes of a 64-hex-digit backup key, or null when it is not one.
     *
     * ⚠ SPACES ARE STRIPPED, BECAUSE WHATSAPP SHOWS THE KEY IN GROUPS OF FOUR and the natural thing
     * to do with a key on a phone screen is read it across in chunks. Refusing a correct key because
     * it was typed the way it was displayed would be the product blaming the user for its own
     * formatting.
     */
    public static KeyBytes = (Hex: string): Uint8Array | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Clean: string = nz(Hex).replace(/\s+/g, "").toLowerCase();
            if (/^[0-9a-f]{64}$/.test(Clean) === false) { return null; }
            const Out: Uint8Array = new Uint8Array(32);
            for (let i: number = 0; i < 32; i = i + 1) {
                Out[i] = parseInt(Clean.substring(i * 2, i * 2 + 2), 16);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "WaCrypt15.KeyBytes");
            return null;
        }
    };

    /**
     * Where the ciphertext starts: 1, plus the feature flag if present, plus the protobuf length.
     *
     * ⚠ THE FLAG BYTE IS NOT COUNTED IN THE LENGTH. It is counted in the MD5 and not in the protobuf,
     * which is exactly the sort of detail that makes a hand-rolled parser land 1 byte out and fail
     * with "the key is wrong" - a refusal pointing at the one thing that was fine.
     */
    public static BodyAt = (File: Uint8Array): number => {
        if (getCoreUtils().KErrorMode === true) { return -1; }
        try {
            if (File.length < 3) { return -1; }
            let At: number = 1;
            if (File[1] === 0x01) { At = 2; }
            const End: number = At + File[0];
            if (End >= File.length) { return -1; }
            return End;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "WaCrypt15.BodyAt");
            return -1;
        }
    };

    /**
     * The 16-byte IV out of the protobuf header: field 3 (`0x1a`), then field 1 (`0x0a`) of length 16.
     *
     * ⚠ NULL MEANS "NOT A CRYPT15", and the caller says so rather than guessing. A crypt14 file
     * carries its IV under field 2 and a crypt12 has no protobuf at all - both are real files a
     * subscriber may pick, and both must be named rather than reported as a bad key.
     */
    public static IvIn = (File: Uint8Array): Uint8Array | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const End: number = WaCrypt15.BodyAt(File);
            if (End < 0) { return null; }
            let At: number = 1;
            if (File[1] === 0x01) { At = 2; }
            for (let i: number = At; i < End - 2; i = i + 1) {
                if (File[i] !== 0x1a) { continue; }
                if (File[i + 2] !== 0x0a || File[i + 3] !== 0x10) { continue; }
                if (i + 4 + 16 > End) { continue; }
                return File.slice(i + 4, i + 20);
            }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "WaCrypt15.IvIn");
            return null;
        }
    };

    /**
     * Open the file. One callback, one answer.
     *
     * ⚠ PROMISES LIVE HERE AND NOWHERE ELSE. `crypto.subtle` has no callback form, so this is the
     * same boundary `kAzureBlobCore` and `kBearerRestExecutor` already draw around `fetch`: the
     * Promise is wrapped once, at the edge, and every caller stays callback-shaped. The handlers are
     * named methods, never inline.
     *
     * ⛔ THE RUN IS BUILT FIRST, BEFORE THE GATE, so that even the early refusals answer through
     * `Crypt15Open.Deliver` - the ONE place that calls `Done`, and only once. A caller whose `Done`
     * throws used to be answered AGAIN from this catch, a success followed by a contradicting
     * refusal, with the caller's own throw blamed on the decryptor.
     */
    public static Open = (File: Uint8Array, Hex: string, Done: Crypt15Done): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: the KErrorMode guard is the SECOND statement, not the first,
        // because it answers through `Run.Refuse` and the run has to exist for that (see above).
        // A bare first-line return would leave the caller waiting for ever.
        const Run: Crypt15Open = new Crypt15Open(File, Done);
        if (getCoreUtils().KErrorMode === true) {
            Run.Refuse("WACommander cannot open a backup at the moment.");
            return;
        }
        try {
            const Key: Uint8Array | null = WaCrypt15.KeyBytes(Hex);
            if (Key === null) {
                Run.Refuse("That key is not right. It is 64 characters long and uses only"
                    + " the digits 0-9 and the letters a-f. Spaces do not matter.");
                return;
            }
            const Iv: Uint8Array | null = WaCrypt15.IvIn(File);
            const At: number = WaCrypt15.BodyAt(File);
            if (Iv === null || At < 0) {
                Run.Refuse("That file is not a crypt15 WhatsApp backup. Pick the file"
                    + " called msgstore.db.crypt15 - an older .crypt14 or .crypt12 will not open.");
                return;
            }
            Run.Begin(At, Iv, Key);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "WaCrypt15.Open");
            Run.Refuse("That backup could not be opened.");
        }
    };
}

/** One file's decryption, held across the two Promise boundaries it has to cross. */
class Crypt15Open {
    private File: Uint8Array;
    private At: number = -1;
    private Iv: Uint8Array = new Uint8Array(0);
    private Key: Uint8Array = new Uint8Array(0);
    private Answer: kSettleOnce<Crypt15Result>;

    /** The decrypted bytes, kept in case they turn out not to be compressed (`OnNotPacked`). */
    private Plain: Uint8Array = new Uint8Array(0);

    /**
     * The refusal every hop gives when the error gate is set - the same sentence `WaCrypt15.Open`
     * gives. ⚠ Each guard ANSWERS through `Refuse` rather than returning bare, because a bare return
     * would leave the caller waiting for ever on a decryption that stopped halfway.
     */
    public static readonly Gated: string = "WACommander cannot open a backup at the moment.";

    public constructor(File: Uint8Array, Done: Crypt15Done) {
        this.File = File;
        this.Answer = new kSettleOnce<Crypt15Result>(Done);
    }

    public Begin = (At: number, Iv: Uint8Array, Key: Uint8Array): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - a bare return would leave the caller
        // waiting for an answer that never comes, which is the one outcome with no recovery.
        try {
            this.At = At;
            this.Iv = Iv;
            this.Key = Key;
            crypto.subtle.importKey("raw", this.Key as BufferSource, "HKDF", false, ["deriveBits"])
                .then(this.OnRoot, this.OnBroken);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "Crypt15Open.Begin");
            this.Refuse("That backup could not be opened.");
        }
    };

    /** HKDF-SHA256, RFC 5869 default salt, the literal context. See `WaCrypt15`'s header. */
    public OnRoot = (Root: CryptoKey): void => {
        if (getCoreUtils().KErrorMode === true) { this.Refuse(Crypt15Open.Gated); return; }
        try {
            crypto.subtle.deriveBits({
                name: "HKDF", hash: "SHA-256", salt: new Uint8Array(32) as BufferSource,
                info: new TextEncoder().encode(WaCrypt15.Info) as BufferSource,
            }, Root, 256).then(this.OnBits, this.OnBroken);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "Crypt15Open.OnRoot");
            this.Refuse("That backup could not be opened.");
        }
    };

    public OnBits = (Bits: ArrayBuffer): void => {
        if (getCoreUtils().KErrorMode === true) { this.Refuse(Crypt15Open.Gated); return; }
        try {
            crypto.subtle.importKey("raw", Bits, "AES-GCM", false, ["decrypt"])
                .then(this.OnKey, this.OnBroken);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "Crypt15Open.OnBits");
            this.Refuse("That backup could not be opened.");
        }
    };

    /**
     * ⚠ THE SLICE ALREADY IS `ciphertext || tag`. The file stores them adjacently and WebCrypto
     * wants them that way, so nothing is concatenated - the last 16 bytes are the MD5 and are
     * simply not handed over.
     */
    public OnKey = (Key: CryptoKey): void => {
        if (getCoreUtils().KErrorMode === true) { this.Refuse(Crypt15Open.Gated); return; }
        try {
            const Body: Uint8Array = this.File.slice(this.At, this.File.length - 16);
            crypto.subtle.decrypt({ name: "AES-GCM", iv: this.Iv as BufferSource, tagLength: 128 },
                Key, Body as BufferSource).then(this.OnPlain, this.OnWrongKey);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "Crypt15Open.OnKey");
            this.Refuse("That backup could not be opened.");
        }
    };

    /**
     * ⛔ A FAILED TAG MEANS THE KEY, AND SAYING SO IS THE WHOLE VALUE OF THIS BRANCH. It is the one
     * error a subscriber will actually hit, and "decryption failed" would send them to look at the
     * file when the file is fine. AES-GCM authenticates, so a tag that verifies IS proof of the key.
     */
    public OnWrongKey = (Why: unknown): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: a rejected tag is an ANSWER - the expected outcome of a
        // mistyped key - not a fault. Reporting it would flip the gate and blank the page over the
        // most ordinary mistake this flow has.
        this.Refuse("That key does not open this backup. Check it against WhatsApp >"
            + " Settings > Chats > Chat backup > End-to-end encrypted backup, and make sure the"
            + " file came from the same phone.");
    };

    /** The plaintext is a zlib stream around the database - see the format note. */
    public OnPlain = (Plain: ArrayBuffer): void => {
        if (getCoreUtils().KErrorMode === true) { this.Refuse(Crypt15Open.Gated); return; }
        try {
            this.Plain = new Uint8Array(Plain);
            const Blob: Response = new Response(Plain);
            const Flat: ReadableStream = (Blob.body as ReadableStream).pipeThrough(
                new DecompressionStream("deflate"));
            new Response(Flat).arrayBuffer().then(this.OnFlat, this.OnNotPacked);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "Crypt15Open.OnPlain");
            this.Refuse("That backup could not be unpacked.");
        }
    };

    /**
     * ⚠ NOT EVERY BACKUP IS COMPRESSED, and the Python does the same thing - it inflates
     * opportunistically and keeps the raw bytes on failure (`wadecrypt.py` 0.1.0, `main`: on
     * `zlib.error`, `output_file = output_decrypted`). Treating "not compressed" as an error would
     * refuse a file that is perfectly good.
     *
     * ⛔ IT HANDS ON THE DECRYPTED PLAINTEXT, NOT AN EMPTY ARRAY. It used to pass `Uint8Array(0)`,
     * so every uncompressed backup was refused as "does not contain a WhatsApp message database" -
     * a verified key and a real database, turned away. `Finish` still proves a database came out,
     * so raw plaintext that is NOT SQLite gets the same refusal as before.
     */
    public OnNotPacked = (Why: unknown): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: an uncompressed backup is a supported shape, not a fault, so
        // the rejection `Why` is not reported. No KErrorMode guard either - a bare return would
        // leave the caller waiting for ever. A THROW here is a fault and IS reported; `Refuse`
        // then answers through `Deliver`, whose shared latch keeps it to exactly one answer.
        try {
            this.Finish(this.Plain);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "Crypt15Open.OnNotPacked");
            this.Refuse("That backup could not be unpacked.");
        }
    };

    public OnFlat = (Flat: ArrayBuffer): void => {
        if (getCoreUtils().KErrorMode === true) { this.Refuse(Crypt15Open.Gated); return; }
        try {
            this.Finish(new Uint8Array(Flat));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "Crypt15Open.OnFlat");
            this.Refuse("That backup could not be unpacked.");
        }
    };

    /**
     * ⛔ IT PROVES A DATABASE CAME OUT. A tag that verified means the key was right, but it says
     * nothing about the file being the one we want - and handing a non-database to the uploader
     * would put a useless object in the subscriber's Drive and fail much later, somewhere else.
     */
    private Finish = (Flat: Uint8Array): void => {
        if (getCoreUtils().KErrorMode === true) { this.Refuse(Crypt15Open.Gated); return; }
        try {
            if (Crypt15Open.IsDb(Flat) === false) {
                this.Refuse("That file opened, but it does not contain a WhatsApp"
                    + " message database. Pick msgstore.db.crypt15 rather than one of the smaller"
                    + " backup files beside it.");
                return;
            }
            const Out: Crypt15Result = new Crypt15Result();
            Out.Db = Flat;
            this.Deliver(Out);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "Crypt15Open.Finish");
            this.Refuse("That backup could not be opened.");
        }
    };

    /** A refusal, answered through `Deliver` like everything else. */
    public Refuse = (Why: string): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - a refusal IS the answer, and a gate
        // here would leave the caller waiting for ever for it.
        try {
            const Out: Crypt15Result = new Crypt15Result();
            Out.Why = Why;
            this.Deliver(Out);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "Crypt15Open.Refuse");
        }
    };

    /**
     * ⛔ THE ONLY PLACE `Done` IS CALLED, AND IT IS CALLED ONCE. The shared latch closes BEFORE the call, so
     * a `Done` that throws lands in this catch and is REPORTED - it is never answered again. The
     * old shape delivered from inside every try and again from every catch, so a caller whose
     * callback threw got its success followed by "That backup could not be unpacked.", and its
     * own bug was blamed on the decryptor.
     */
    private Deliver = (Out: Crypt15Result): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - this is the only thing that answers
        // the caller, and a guard would leave it waiting for ever. The catch only reports.
        try {
            this.Answer.Settle(Out);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "Crypt15Open.Deliver");
        }
    };

    public static IsDb = (Flat: Uint8Array): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Flat.length < 16) { return false; }
            const Head: string = new TextDecoder().decode(Flat.slice(0, 15));
            return Head === WaCrypt15.Magic;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "Crypt15Open.IsDb");
            return false;
        }
    };

    /** A crypto primitive REJECTED - a browser fault, not an answer, so it is reported. */
    public OnBroken = (Why: unknown): void => {
        if (getCoreUtils().KErrorMode === true) { this.Refuse(Crypt15Open.Gated); return; }
        try {
            getCoreUtils().KGlobalErrorHandler(Why, "Crypt15Open.OnBroken");
            this.Refuse("This browser could not open the backup.");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "Crypt15Open.OnBroken");
            this.Refuse("This browser could not open the backup.");
        }
    };
}
